// v1.8 P0: 5 红线检查 — 与 docs/WORKFLOW.md § 2.14 红线 5 必背 对齐
// 数据: apps/web/src/lib/workflow.ts (REDLINES)
// 交互: 每项可点击展开详细解释
// 状态: critical=red, important=warn, advisory=info
import { useState } from 'react';
import { REDLINES } from '../lib/workflow';
import { Icon } from './Icon';

const SEVERITY_BADGE: Record<string, { text: string; cls: string }> = {
  critical: { text: '资金', cls: 'bg-bad/15 text-bad' },
  important: { text: '合规', cls: 'bg-warn/15 text-warn' },
  advisory: { text: '流程', cls: 'bg-info/15 text-info' },
};

export function RedlineCheck() {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  function toggle(id: string) {
    const next = new Set(expanded);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setExpanded(next);
  }

  function acknowledge(id: string) {
    const next = new Set(dismissed);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setDismissed(next);
  }

  const active = REDLINES.filter((r) => !dismissed.has(r.id));
  const acknowledged = REDLINES.filter((r) => dismissed.has(r.id));

  return (
    <div className="bg-surface border border-line rounded p-5 mb-4">
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <h2 className="text-sm font-semibold flex items-center gap-1.5">
          <Icon name="siren" size="sm" className="text-bad" />
          本次订单红线检查 (5 必背)
        </h2>
        <span className="text-xs text-ink-3 flex items-center gap-1">
          {active.length === 0 ? (
            <>
              <Icon name="check-circle-2" size="xs" strokeWidth={2} className="text-good" />
              全部已确认
            </>
          ) : (
            `${active.length} 项待确认`
          )}
        </span>
      </div>

      <p className="text-xs text-ink-3 mb-3 leading-relaxed">
        资金 / 合规 / 复盘 — 三角架构外贸的 5 条铁律, 每单必查. 展开查看详细解释, 确认后折叠.
      </p>

      {active.length > 0 && (
        <ul className="space-y-1.5">
          {active.map((r) => {
            const sev = SEVERITY_BADGE[r.severity];
            const isOpen = expanded.has(r.id);
            return (
              <li
                key={r.id}
                className={`border rounded ${
                  r.severity === 'critical'
                    ? 'border-bad/40 bg-bad/5'
                    : r.severity === 'important'
                      ? 'border-warn/40 bg-warn/5'
                      : 'border-line'
                }`}
              >
                <div className="flex items-stretch">
                  <button
                    type="button"
                    onClick={() => toggle(r.id)}
                    className="flex-1 flex items-center gap-2.5 p-2.5 text-left hover:bg-surface-2/50 transition-colors min-w-0"
                  >
                    <span
                      className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isOpen
                          ? 'border-bad text-bad'
                          : r.severity === 'critical'
                            ? 'border-bad text-bad'
                            : r.severity === 'important'
                              ? 'border-warn text-warn'
                              : 'border-line text-ink-3'
                      }`}
                      aria-hidden="true"
                    >
                      {isOpen ? (
                        <Icon name="minus" size="xs" strokeWidth={2.5} />
                      ) : (
                        <Icon name="plus" size="xs" strokeWidth={2.5} />
                      )}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-medium text-ink-1">{r.shortZh}</span>
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded ${sev?.cls ?? 'bg-surface-2'}`}
                        >
                          {sev?.text ?? r.severity}
                        </span>
                      </div>
                      {isOpen && (
                        <div className="mt-1.5 text-xs text-ink-2 leading-relaxed">{r.bodyZh}</div>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => acknowledge(r.id)}
                    className="shrink-0 m-2 px-2 py-1 text-[11px] border border-line rounded hover:bg-good/10 hover:border-good hover:text-good cursor-pointer self-start flex items-center gap-1"
                    title="点击确认已读"
                  >
                    <Icon name="check" size="xs" strokeWidth={2} />
                    已确认
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {acknowledged.length > 0 && (
        <details className="mt-3">
          <summary className="text-xs text-ink-3 cursor-pointer hover:text-ink-2 flex items-center gap-1.5">
            <Icon name="check-circle-2" size="xs" strokeWidth={2} className="text-good" />
            已确认 ({acknowledged.length})
          </summary>
          <ul className="mt-2 space-y-1">
            {acknowledged.map((r) => (
              <li key={r.id} className="text-xs text-ink-3 flex items-center gap-2 line-through">
                <Icon name="check" size="xs" strokeWidth={2} className="text-good" />
                {r.shortZh}
                <button
                  type="button"
                  onClick={() => acknowledge(r.id)}
                  className="text-ink-3 hover:text-ink-1 no-underline"
                  title="撤销"
                >
                  撤销
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
